<template>
  <ContratoPendenteBanner
    v-if="hasContratoPendente"
    :navigation-mode="navigationMode"
    :vue-base-url="vueBaseUrl"
  />
  <ContratoExpiradoModalBloqueio
    v-if="podeAssinarTermoPendente"
    v-model="showAvisoContratoPendente"
    :bloqueado="isBloqueioPorPrazo"
    @assinar-contrato="irParaAssinaturaContrato"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { F360Product } from "@/core/@types/types";
import useHostNavigation from "@/core/composables/useHostNavigation";
import useF360Router from "@/core/router/useF360Router";
import { EGestaoContratos } from "@/features/financas/views/gestaoContratos/router/types";
import { useRoute, type RouteLocationNormalized } from "vue-router";
import ContratoPendenteBanner from "@/ui/components/Layout/Navbar/MenuDeAcoes/ContratoPendente/ContratoPendenteBanner.vue";
import ContratoExpiradoModalBloqueio from "@/ui/components/Layout/Navbar/MenuDeAcoes/ContratoPendente/ContratoExpiradoModalBloqueio.vue";
import { useContratoPendenteStore } from "@/core/store/contratos/useContratoPendenteStore";
import { useCampanhaStore } from "@/features/shared/Campanhas/store/useCampanhaStore";

const props = withDefaults(
  defineProps<{
    navigationMode?: "vue" | "mvc";
    vueBaseUrl?: string;
    autoFetch?: boolean;
  }>(),
  {
    navigationMode: "vue",
    vueBaseUrl: "/v2/#",
    autoFetch: true
  }
);

const contratoPendenteStore = useContratoPendenteStore();
const campanhaStore = useCampanhaStore();
const route = useRoute();
const avisoContratoExpiradoFechado = ref(false);
const showAvisoRecorrenteContratoPendente = ref(false);
const avisoAdiadoPorCampanha = ref(false);
const { handlePrefixName } = useF360Router();
const { navigateTo } = useHostNavigation({
  navigationMode: () => props.navigationMode,
  vueBaseUrl: () => props.vueBaseUrl
});

const QUERY_ABRIR_CONTRATO_PENDENTE = "abrirContratoPendente";
const GESTAO_CONTRATOS_PATH = "/gestao-de-contratos";
const CAMPANHAS_MVC_STATE_EVENT = "f360:campanhas-state-change";
const PAPEIS_USUARIO_CARREGADOS_EVENT = "f360:user-roles-loaded";
type CampanhasMvcState = {
  verificacaoConcluida: boolean;
  popUpAberto: boolean;
};
type WindowWithCampanhasState = Window & {
  F360CampanhasState?: CampanhasMvcState;
};
const initialCampanhasMvcState = (window as WindowWithCampanhasState)
  .F360CampanhasState;
const verificacaoCampanhasMvcConcluida = ref(
  initialCampanhasMvcState?.verificacaoConcluida ?? false
);
const campanhaMarketingMvcAberta = ref(
  initialCampanhasMvcState?.popUpAberto ?? false
);
const gestaoContratosRouteName = handlePrefixName(
  F360Product.Financas,
  EGestaoContratos.Index
);

const {
  isPendente: hasContratoPendente,
  isBloqueioPorPrazo,
  isPrazoAceiteExpirado,
  showModalBloqueio: showModalBloqueioContratoPendente,
  podeConsultarPendencia,
  podeAssinarTermoPendente,
  lastTerm
} = storeToRefs(contratoPendenteStore);
const { verificacaoConcluida, popUpAberto } = storeToRefs(campanhaStore);
const verificacaoCampanhasConcluida = computed(() =>
  props.navigationMode === "mvc"
    ? verificacaoCampanhasMvcConcluida.value
    : verificacaoConcluida.value
);
const campanhaMarketingAberta = computed(() =>
  props.navigationMode === "mvc"
    ? campanhaMarketingMvcAberta.value
    : popUpAberto.value
);

const isRotaGestaoContratosAtual = computed(() => isRotaGestaoContratos(route));

const showAvisoContratoPendente = computed({
  get() {
    if (
      !podeAssinarTermoPendente.value ||
      isRotaGestaoContratosAtual.value
    ) {
      return false;
    }

    if (
      !isBloqueioPorPrazo.value &&
      (!verificacaoCampanhasConcluida.value ||
        campanhaMarketingAberta.value ||
        avisoAdiadoPorCampanha.value)
    ) {
      return false;
    }

    if (isPrazoAceiteExpirado.value) {
      return isBloqueioPorPrazo.value
        ? showModalBloqueioContratoPendente.value
        : !avisoContratoExpiradoFechado.value;
    }

    return showAvisoRecorrenteContratoPendente.value;
  },
  set(value: boolean) {
    if (isBloqueioPorPrazo.value) {
      showModalBloqueioContratoPendente.value = true;

      return;
    }

    if (!isPrazoAceiteExpirado.value) {
      showAvisoRecorrenteContratoPendente.value = value;

      return;
    }

    avisoContratoExpiradoFechado.value = !value;
  }
});

function normalizePath(path: string) {
  return path.replace(/\/$/, "");
}

function isRotaGestaoContratos(target: Pick<RouteLocationNormalized, "path">) {
  return normalizePath(target.path) === GESTAO_CONTRATOS_PATH;
}

function abrirModalBloqueioSeNecessario() {
  if (
    !podeAssinarTermoPendente.value ||
    !isBloqueioPorPrazo.value ||
    isRotaGestaoContratosAtual.value
  ) {
    return;
  }

  showModalBloqueioContratoPendente.value = true;
}

function irParaAssinaturaContrato() {
  if (!podeAssinarTermoPendente.value) {
    return;
  }

  contratoPendenteStore.iniciarAssinaturaContrato();

  void navigateTo({
    name: gestaoContratosRouteName,
    query: {
      [QUERY_ABRIR_CONTRATO_PENDENTE]: "1"
    }
  });
}

function atualizarExibicaoAvisoRecorrente() {
  if (isPrazoAceiteExpirado.value) {
    showAvisoRecorrenteContratoPendente.value = false;

    return;
  }

  const deveExibir = contratoPendenteStore.podeExibirAvisoContratoPendente;

  showAvisoRecorrenteContratoPendente.value = deveExibir;

  if (deveExibir) {
    contratoPendenteStore.registrarExibicaoAvisoContratoPendente();
  }
}

function coordenarAvisoComCampanha() {
  if (isBloqueioPorPrazo.value) {
    avisoAdiadoPorCampanha.value = false;

    return;
  }

  if (!verificacaoCampanhasConcluida.value) {
    showAvisoRecorrenteContratoPendente.value = false;

    return;
  }

  if (campanhaMarketingAberta.value) {
    contratoPendenteStore.adiarAvisoContratoPendenteParaProximoDia();
    avisoAdiadoPorCampanha.value = true;
    showAvisoRecorrenteContratoPendente.value = false;

    return;
  }

  if (!avisoAdiadoPorCampanha.value) {
    atualizarExibicaoAvisoRecorrente();
  }
}

function handleCampanhasMvcStateChange(event: Event) {
  const state = (event as CustomEvent<CampanhasMvcState>).detail;

  if (!state) {
    return;
  }

  verificacaoCampanhasMvcConcluida.value = state.verificacaoConcluida;
  campanhaMarketingMvcAberta.value = state.popUpAberto;
}

function handlePapeisUsuarioCarregados() {
  contratoPendenteStore.reavaliarPermissoesUsuario();
}

onMounted(() => {
  if (props.navigationMode === "mvc") {
    window.addEventListener(
      CAMPANHAS_MVC_STATE_EVENT,
      handleCampanhasMvcStateChange
    );
    window.addEventListener(
      PAPEIS_USUARIO_CARREGADOS_EVENT,
      handlePapeisUsuarioCarregados
    );
    contratoPendenteStore.reavaliarPermissoesUsuario();
  }

  if (props.autoFetch && podeConsultarPendencia.value) {
    contratoPendenteStore.fetchContratoPendente();
  }
});

watch(
  [isBloqueioPorPrazo, isRotaGestaoContratosAtual, podeAssinarTermoPendente],
  abrirModalBloqueioSeNecessario,
  { immediate: true }
);

watch(
  () => lastTerm.value?.id,
  () => {
    avisoContratoExpiradoFechado.value = false;
  }
);

watch(
  [hasContratoPendente, isPrazoAceiteExpirado, () => lastTerm.value?.id],
  coordenarAvisoComCampanha,
  { immediate: true }
);

watch(
  [verificacaoCampanhasConcluida, campanhaMarketingAberta],
  coordenarAvisoComCampanha
);

watch(
  () => route.path,
  (path, oldPath) => {
    if (!oldPath || !isRotaGestaoContratos({ path: oldPath })) {
      return;
    }

    if (!isRotaGestaoContratos({ path })) {
      avisoContratoExpiradoFechado.value = false;
      atualizarExibicaoAvisoRecorrente();
    }
  }
);

watch(showModalBloqueioContratoPendente, abrirModalBloqueioSeNecessario);

onUnmounted(() => {
  if (props.navigationMode === "mvc") {
    window.removeEventListener(
      PAPEIS_USUARIO_CARREGADOS_EVENT,
      handlePapeisUsuarioCarregados
    );
  }
  window.removeEventListener(
    CAMPANHAS_MVC_STATE_EVENT,
    handleCampanhasMvcStateChange
  );
});
</script>
